import React from 'react';
import { Alert, Typography } from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import GestionCard from './GestionCard';
import {
	DepartamentosContainer,
	DepartamentosGrid,
	PageHeader,
	AddButton,
	PageNotice,
} from './styles';
import { ISubject, IAcademy, IDepartment } from '../../types/departamento';

interface DepartamentosPageViewProps {
	departments: IDepartment[];
	academies: IAcademy[];
	subjects: ISubject[];
	selectedDepartment: IDepartment | null;
	selectedAcademy: IAcademy | null;
	onDepartmentClick: (department: IDepartment) => void;
	onAcademyClick: (academy: IAcademy) => void;
	onEditDepartment: (dept: IDepartment) => void;
	onDeleteDepartment: (dept: IDepartment) => void;
	onEditAcademy: (acad: IAcademy) => void;
	onDeleteAcademy: (acad: IAcademy) => void;
	onEditSubject: (subj: ISubject) => void;
	onDeleteSubject: (subj: ISubject) => void;
	academyCountByDeptId: Record<number, number>;
	subjectCountByAcadId: Record<number, number>;
	onAddClick: () => void;
	isLoading: boolean;
	loadError: string | null;
}

const DepartamentosPageView: React.FC<DepartamentosPageViewProps> = ({
	departments,
	academies,
	subjects,
	selectedDepartment,
	selectedAcademy,
	onDepartmentClick,
	onAcademyClick,
	onEditDepartment,
	onDeleteDepartment,
	onEditAcademy,
	onDeleteAcademy,
	onEditSubject,
	onDeleteSubject,
	academyCountByDeptId,
	subjectCountByAcadId,
	onAddClick,
	isLoading,
	loadError,
}) => {
	const getAddButtonLabel = (): string => {
		if (selectedDepartment && selectedAcademy) return 'Añadir materia';
		if (selectedDepartment) return 'Añadir academia';
		return 'Añadir departamento';
	};

	const renderContent = () => {
		// Level 3: Show subjects (no drill-down)
		if (selectedDepartment && selectedAcademy) {
			return subjects.map((subject) => (
				<GestionCard
					key={subject.id}
					name={subject.name}
					color={selectedDepartment.color}
					onEdit={() => onEditSubject(subject)}
					onDelete={() => onDeleteSubject(subject)}
				/>
			));
		}

		// Level 2: Show academies → drill down to subjects
		if (selectedDepartment) {
			return academies.map((academy) => (
				<GestionCard
					key={academy.id}
					name={academy.name}
					color={selectedDepartment.color}
					childCount={subjectCountByAcadId[academy.id] ?? 0}
					childLabel='materia'
					onSelect={() => onAcademyClick(academy)}
					onEdit={() => onEditAcademy(academy)}
					onDelete={() => onDeleteAcademy(academy)}
				/>
			));
		}

		// Level 1: Show departments → drill down to academies
		return departments.map((department) => (
				<GestionCard
					key={department.id}
					name={department.name}
					description={department.description}
					color={department.color}
					imageUrl={department.imageUrl}
				childCount={academyCountByDeptId[department.id] ?? 0}
				childLabel='academia'
				onSelect={() => onDepartmentClick(department)}
				onEdit={() => onEditDepartment(department)}
				onDelete={() => onDeleteDepartment(department)}
			/>
		));
	};

	const visibleListEmpty = selectedAcademy
		? subjects.length === 0
		: selectedDepartment
			? academies.length === 0
			: departments.length === 0;

	return (
		<DepartamentosContainer>
			{loadError && (
				<PageNotice>
					<Alert severity='error'>{loadError}</Alert>
				</PageNotice>
			)}
			<PageHeader>
				<AddButton
					variant='contained'
					color='primary'
					startIcon={<AddRounded />}
					onClick={onAddClick}
				>
					{getAddButtonLabel()}
				</AddButton>
			</PageHeader>
			{isLoading && visibleListEmpty ? (
				<Typography color='text.secondary'>Cargando...</Typography>
			) : (
				<DepartamentosGrid>{renderContent()}</DepartamentosGrid>
			)}
		</DepartamentosContainer>
	);
};

export default DepartamentosPageView;
